"use client";

import { Container, Heading, Image, Text } from "@pamperedchef/design-system";

import * as styles from "./CategoryBanner.module.scss";

export interface CategoryBannerImage {
	src: string;
	alt: string;
}

export interface CategoryBannerProps {
	/** Sits above the headline — the parent's name, or a fixed word on a top-level page. */
	eyebrow?: string;
	headline: string;
	/** One or two sentences. Plain text; the caller flattens any rich text. */
	description?: string;
	/** Omit for the text-only band, which is what a subcategory page shows. */
	image?: CategoryBannerImage;
	/** Centred is the text-only band; start is the band that carries an image. */
	align?: "start" | "center";
}

/**
 * The band at the top of a category page. Presentational and props-in, so the
 * same band serves a super category, a category, and later a collection.
 */
export function CategoryBanner({
	eyebrow,
	headline,
	description,
	image,
	align = "start"
}: CategoryBannerProps): JSX.Element {
	return (
		<section
			className={[styles["banner"], styles[`banner--${align}`]].join(" ")}
			data-component="CategoryBanner"
			data-has-image={image ? "true" : "false"}
		>
			<Container>
				<div className={styles["banner__inner"]}>
					<div className={styles["banner__copy"]}>
						{eyebrow ? (
							<Text tag="p" className={styles["banner__eyebrow"]}>
								{eyebrow}
							</Text>
						) : null}

						<Heading
							tag="h1"
							className={styles["banner__headline"]}
						>
							{headline}
						</Heading>

						{description ? (
							<Text
								tag="p"
								className={styles["banner__description"]}
							>
								{description}
							</Text>
						) : null}
					</div>

					{image ? (
						<Image
							src={image.src}
							alt={image.alt}
							className={styles["banner__image"]}
						/>
					) : null}
				</div>
			</Container>
		</section>
	);
}
